import { STYLES } from "@/lib/styles";
import { Sparkles } from "lucide-react";

/**
 * StyleBlendPreview — a tiny composite thumbnail that hints at what a
 * multi-style blend will feel like. Uses each selected style's cached sample
 * thumbnail and layers them with CSS mix-blend-modes so users can gut-check
 * their pick without spending a credit.
 *
 * Layout:
 *   • 1 style   → single crisp thumbnail
 *   • 2 styles  → diagonal-split composite (top-left ↔ bottom-right)
 *   • 3 styles  → tri-slice (three vertical panels, each with a soft-light overlay)
 *   • 4+ styles → 2x2 tiled grid of the first 4, with a "+N" badge on top
 *
 * Props:
 *   ids: string[]  — ordered selected style ids
 *   size: number   — square edge in px (default 96)
 *   testId
 */
export function StyleBlendPreview({ ids = [], size = 96, testId = "style-blend-preview" }) {
  const selected = ids
    .map((id) => STYLES.find((s) => s.id === id))
    .filter(Boolean);

  if (selected.length === 0) return null;

  const dim = { width: size, height: size };

  // ── Single style: crisp thumbnail
  if (selected.length === 1) {
    const s = selected[0];
    return (
      <div
        data-testid={testId}
        className="relative rounded-xl overflow-hidden border border-[#F2C94C]/50 shadow-[0_0_18px_-6px_rgba(242,201,76,0.7)] flex-shrink-0"
        style={dim}
        title={`Preview · ${s.name}`}
      >
        <img src={s.thumb} alt={s.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-x-0 bottom-0 px-1.5 py-1 bg-gradient-to-t from-[#0A0A0A] to-transparent">
          <div className="font-mono-hy text-[8px] text-[#F2C94C] tracking-widest truncate">{s.name}</div>
        </div>
      </div>
    );
  }

  // ── 2 styles: diagonal split
  if (selected.length === 2) {
    return (
      <div
        data-testid={testId}
        className="relative rounded-xl overflow-hidden border border-[#F2C94C]/50 shadow-[0_0_22px_-6px_rgba(242,201,76,0.75)] flex-shrink-0"
        style={dim}
        title={`Blend preview · ${selected.map((s) => s.name).join(" + ")}`}
      >
        <img
          src={selected[0].thumb}
          alt={selected[0].name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <img
          src={selected[1].thumb}
          alt={selected[1].name}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ clipPath: "polygon(100% 0, 100% 100%, 0 100%)", mixBlendMode: "screen", opacity: 0.85 }}
        />
        {/* Diagonal seam highlight */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background: "linear-gradient(135deg, transparent 48%, rgba(242,201,76,0.55) 49%, rgba(0,229,255,0.55) 51%, transparent 52%)",
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className="font-mono-hy text-[8px] text-white bg-[#0A0A0A]/70 border border-[#F2C94C]/40 rounded-full px-1.5 py-0.5 tracking-widest"
            data-testid={`${testId}-badge`}
          >
            2× BLEND
          </span>
        </div>
      </div>
    );
  }

  // ── 3 styles: tri-slice with soft-light overlays
  if (selected.length === 3) {
    return (
      <div
        data-testid={testId}
        className="relative rounded-xl overflow-hidden border border-[#F2C94C]/50 shadow-[0_0_22px_-6px_rgba(242,201,76,0.75)] flex-shrink-0"
        style={dim}
        title={`Blend preview · ${selected.map((s) => s.name).join(" + ")}`}
      >
        <div className="absolute inset-0 grid grid-cols-3 gap-0">
          {selected.map((s, i) => (
            <div key={s.id} className="relative overflow-hidden">
              <img
                src={s.thumb}
                alt={s.name}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ transform: `scale(${1.4}) translateX(${(i - 1) * 6}%)` }}
              />
            </div>
          ))}
        </div>
        {/* Cohesion overlay — soft-light gold veil to feel like a fusion */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background: "linear-gradient(135deg, rgba(242,201,76,0.35), rgba(0,229,255,0.25), rgba(226,125,96,0.30))",
            mixBlendMode: "soft-light",
          }}
        />
        {/* Seam highlights */}
        <div aria-hidden className="absolute inset-y-0 left-[33.33%] w-px bg-[#F2C94C]/40" />
        <div aria-hidden className="absolute inset-y-0 left-[66.66%] w-px bg-[#00E5FF]/40" />
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className="font-mono-hy text-[8px] text-white bg-[#0A0A0A]/70 border border-[#F2C94C]/40 rounded-full px-1.5 py-0.5 tracking-widest"
            data-testid={`${testId}-badge`}
          >
            3× FUSION
          </span>
        </div>
      </div>
    );
  }

  // ── 4+ styles: 2x2 tile + "+N" badge
  const first4 = selected.slice(0, 4);
  const extra = selected.length - 4;
  return (
    <div
      data-testid={testId}
      className="relative rounded-xl overflow-hidden border border-[#F2C94C]/50 shadow-[0_0_22px_-6px_rgba(242,201,76,0.75)] flex-shrink-0"
      style={dim}
      title={`Blend preview · ${selected.length} styles`}
    >
      <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-0">
        {first4.map((s, i) => (
          <div key={s.id} className="relative overflow-hidden">
            <img
              src={s.thumb}
              alt={s.name}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ opacity: 0.9 - i * 0.05 }}
            />
          </div>
        ))}
      </div>
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background: "linear-gradient(135deg, rgba(242,201,76,0.30), rgba(0,229,255,0.25), rgba(226,125,96,0.25), rgba(235,87,87,0.20))",
          mixBlendMode: "soft-light",
        }}
      />
      <div aria-hidden className="absolute inset-x-0 top-1/2 h-px bg-[#F2C94C]/30" />
      <div aria-hidden className="absolute inset-y-0 left-1/2 w-px bg-[#00E5FF]/30" />
      <div className="absolute inset-0 flex items-center justify-center gap-1">
        <Sparkles className="w-3 h-3 text-[#F2C94C]" />
        <span
          className="font-mono-hy text-[8px] text-white bg-[#0A0A0A]/70 border border-[#F2C94C]/40 rounded-full px-1.5 py-0.5 tracking-widest"
          data-testid={`${testId}-badge`}
        >
          {selected.length}× FUSION
        </span>
      </div>
      {extra > 0 && (
        <div className="absolute top-1 right-1 font-mono-hy text-[8px] bg-[#F2C94C] text-[#0A0A0A] rounded-full px-1.5 py-0.5 tracking-widest">
          +{extra}
        </div>
      )}
    </div>
  );
}

export default StyleBlendPreview;
